import { useEffect, useMemo, useState } from "react";
import { Plus, Power, Save, Trash2, UserCog, Users } from "lucide-react";
import { toast } from "sonner";
import { STAFF_WARDS } from "@/lib/patient";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Loading } from "@/components/common";
import { Avatar } from "@/components/Avatar";
import { Input } from "@/components/ui/input";
import {
  Action,
  Cell,
  DataTable,
  EmptyState,
  Field,
  PageHeader,
  Panel,
  PanelHeader,
  Row,
  SearchField,
  SelectField,
  StatusBadge,
} from "./ui";

export const STAFF_TYPE_LABELS = {
  conselheiro: "Conselheiro",
  psicologo: "Psicólogo(a)",
  psiquiatra: "Psiquiatra",
  administracao: "Administração",
  farmacia: "Farmácia",
  diretoria: "Diretoria",
  apoio: "Apoio",
  enfermagem: "Enfermagem",
  nutricionista: "Nutricionista",
  arteterapeuta: "Arteterapeuta",
  assistente_social: "Assistente Social",
  educador_fisico: "Educador Físico",
  farmaceutico: "Farmacêutico",
};

const ROLE_LABELS = {
  clinical_coordinator: "Coordenação clínica",
  clinician: "Profissional clínico",
  admin: "Administração (acesso total)",
  auditor: "Auditoria",
  content_manager: "Gestor de conteúdo",
  commercial: "Comercial / Admissões",
};

const EMPTY_FORM = {
  name: "",
  username: "",
  email: "",
  staff_type: "conselheiro",
  role: "clinician",
  ward: "",
  password: "",
  password_confirmation: "",
  active: true,
};

export default function StaffManager() {
  const { api, user, checkAuth } = useAuth();
  const [staff, setStaff] = useState(null);
  const [denied, setDenied] = useState(false);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [confirmRemovePhoto, setConfirmRemovePhoto] = useState(false);
  const [uploading, setUploading] = useState(false);

  const clearPhotoState = () => {
    setPhotoFile(null);
    setPreviewSrc(null);
    setCurrentPhoto(null);
    setConfirmRemovePhoto(false);
  };

  const pickPhoto = (file) => {
    if (!file) return;
    setPhotoFile(file);
    setConfirmRemovePhoto(false);
    try {
      setPreviewSrc(URL.createObjectURL(file));
    } catch {
      setPreviewSrc(null);
    }
  };

  const sendPhoto = async (staffId, file) => {
    const body = new FormData();
    body.append("file", file, file.name || "foto.jpg");
    const { data } = await api.post(`/portal/staff/${staffId}/photo`, body);
    return data;
  };

  const uploadPhotoNow = async (staffId, file) => {
    setUploading(true);
    try {
      const updated = await sendPhoto(staffId, file);
      setCurrentPhoto(updated.photo_thumb_url);
      setPhotoFile(null);
      setPreviewSrc(null);
      toast.success("Foto atualizada");
      await load();
      if (staffId === user?.id) await checkAuth();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setUploading(false);
  };

  const removePhoto = async (staffId) => {
    setUploading(true);
    try {
      await api.delete(`/portal/staff/${staffId}/photo`);
      setCurrentPhoto(null);
      setPhotoFile(null);
      setPreviewSrc(null);
      setConfirmRemovePhoto(false);
      toast.success("Foto removida");
      await load();
      if (staffId === user?.id) await checkAuth();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setUploading(false);
  };

  const load = async () => {
    try {
      const { data } = await api.get("/portal/staff");
      setStaff(data);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(e.response?.data?.detail));
      setStaff([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCreate = () => {
    setEditing("new");
    setForm(EMPTY_FORM);
    clearPhotoState();
  };

  const startEdit = (person) => {
    setEditing(person.id);
    clearPhotoState();
    setCurrentPhoto(person.photo_thumb_url || null);
    setForm({
      name: person.name,
      username: person.username || "",
      email: person.email || "",
      staff_type: person.staff_type || "conselheiro",
      ward: person.ward || "",
      role: person.role,
      password: "",
      password_confirmation: "",
      active: person.status === "active",
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        const { data: created } = await api.post("/portal/staff", form);
        if (photoFile) {
          try {
            await sendPhoto(created.id, photoFile);
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
          }
        }
        toast.success("Funcionário cadastrado");
      } else {
        const payload = {
          name: form.name,
          username: form.username,
          email: form.email || null,
          staff_type: form.staff_type,
          role: form.role,
          ward: form.ward || "",
        };
        if (form.password) {
          payload.password = form.password;
          payload.password_confirmation = form.password_confirmation;
        }
        await api.put(`/portal/staff/${editing}`, payload);
        toast.success("Funcionário atualizado");
      }
      setEditing(null);
      setForm(EMPTY_FORM);
      clearPhotoState();
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setSaving(false);
  };

  const toggleStatus = async (person) => {
    try {
      await api.post(`/portal/staff/${person.id}/status`, { active: person.status !== "active" });
      toast.success(person.status === "active" ? "Acesso desativado" : "Acesso ativado");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (staff || []).filter((p) =>
      !term ? true : `${p.name} ${p.username || ""} ${p.email || ""}`.toLowerCase().includes(term)
    );
  }, [staff, query]);

  if (denied)
    return (
      <div data-testid="portal-staff-page" className="space-y-7">
        <PageHeader kicker="Acesso interno" title="Funcionários" />
        <EmptyState
          testId="staff-denied"
          icon={UserCog}
          title="Permissão insuficiente"
          description="A gestão de funcionários é restrita ao perfil administrativo. Fale com a Diretoria para solicitar acesso."
        />
      </div>
    );

  if (staff === null) return <Loading label="Carregando funcionários…" />;

  return (
    <div data-testid="portal-staff-page" className="space-y-7">
      <PageHeader
        kicker="Acesso interno"
        title="Funcionários"
        subtitle="Contas que acessam o Portal Erimus. Nunca excluímos contas com histórico: o acesso é ativado ou desativado."
        actions={
          <Action icon={Plus} data-testid="staff-new-button" onClick={startCreate}>
            Novo funcionário
          </Action>
        }
      />

      {editing ? (
        <Panel testId="staff-editor">
          <PanelHeader
            label={editing === "new" ? "Cadastro" : "Edição"}
            title={editing === "new" ? "Novo funcionário" : form.name}
            hint="O tipo de funcionário é uma classificação. O papel de acesso define o que a pessoa pode ver no portal."
          />
          <form onSubmit={save} className="mt-6 grid gap-5 sm:grid-cols-2">
            <Field label="Nome completo" htmlFor="staff-name">
              <Input
                id="staff-name"
                data-testid="staff-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-11 rounded-xl text-[14.5px]"
                required
              />
            </Field>
            <Field label="Nome de usuário" htmlFor="staff-username" hint="Sem espaços. Letras, números, ponto, hífen e sublinhado.">
              <Input
                id="staff-username"
                data-testid="staff-username-input"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value.replace(/\s/g, "").toLowerCase() })}
                className="h-11 rounded-xl text-[14.5px]"
                required
              />
            </Field>
            <Field label="E-mail (opcional)" htmlFor="staff-email">
              <Input
                id="staff-email"
                data-testid="staff-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="h-11 rounded-xl text-[14.5px]"
              />
            </Field>
            <Field label="Tipo de funcionário">
              <SelectField
                testId="staff-type-select"
                value={form.staff_type}
                onChange={(e) => setForm({ ...form, staff_type: e.target.value })}
                className="w-full"
              >
                {Object.entries(STAFF_TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </SelectField>
            </Field>
            <Field label="Ala" hint="Ala em que o funcionário atua.">
              <SelectField
                testId="staff-ward-select"
                value={form.ward}
                onChange={(e) => setForm({ ...form, ward: e.target.value })}
                className="w-full"
              >
                <option value="">Não informada</option>
                {STAFF_WARDS.map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </SelectField>
            </Field>
            <Field label="Papel de acesso">
              <SelectField
                testId="staff-role-select"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full"
              >
                {Object.entries(ROLE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </SelectField>
            </Field>
            <Field label="Status">
              <SelectField
                testId="staff-status-select"
                value={form.active ? "active" : "disabled"}
                onChange={(e) => setForm({ ...form, active: e.target.value === "active" })}
                className="w-full"
              >
                <option value="active">Ativo</option>
                <option value="disabled">Inativo</option>
              </SelectField>
            </Field>
            <Field
              label={editing === "new" ? "Senha" : "Nova senha (opcional)"}
              htmlFor="staff-password"
              hint="Mínimo de 8 caracteres."
            >
              <Input
                id="staff-password"
                data-testid="staff-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="h-11 rounded-xl text-[14.5px]"
                required={editing === "new"}
              />
            </Field>
            <Field label="Confirmação da senha" htmlFor="staff-password-confirmation">
              <Input
                id="staff-password-confirmation"
                data-testid="staff-password-confirmation-input"
                type="password"
                value={form.password_confirmation}
                onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })}
                className="h-11 rounded-xl text-[14.5px]"
                required={editing === "new"}
              />
            </Field>
            <Field
              label="Foto do funcionário (opcional)"
              hint="JPEG, PNG, WebP ou HEIC/HEIF. A imagem é recortada em quadrado e convertida automaticamente."
            >
              <div className="flex flex-wrap items-center gap-3.5">
                {previewSrc ? (
                  <img
                    data-testid="staff-photo-preview"
                    src={previewSrc}
                    alt="Prévia da foto"
                    className="h-16 w-16 shrink-0 rounded-full border border-border object-cover"
                  />
                ) : (
                  <Avatar name={form.name} photoUrl={currentPhoto} size={64} testId="staff-photo-current" />
                )}
                <div className="flex flex-wrap gap-2.5">
                  <label
                    className={`inline-flex min-h-[38px] items-center rounded-full border border-border bg-white px-4 text-[13.5px] font-semibold text-ink-700 ${
                      uploading ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-sand"
                    }`}
                  >
                    {currentPhoto || previewSrc ? "Substituir foto" : "Escolher foto"}
                    <input
                      type="file"
                      accept="image/*,.heic,.heif"
                      data-testid="staff-photo-input"
                      disabled={uploading}
                      className="hidden"
                      onChange={(e) => {
                        pickPhoto(e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {photoFile && editing !== "new" ? (
                    <Action
                      type="button"
                      size="sm"
                      data-testid="staff-photo-upload"
                      disabled={uploading}
                      onClick={() => uploadPhotoNow(editing, photoFile)}
                    >
                      {uploading ? "Enviando…" : "Salvar foto"}
                    </Action>
                  ) : null}
                  {photoFile ? (
                    <Action
                      type="button"
                      size="sm"
                      variant="secondary"
                      data-testid="staff-photo-discard"
                      onClick={() => {
                        setPhotoFile(null);
                        setPreviewSrc(null);
                      }}
                    >
                      Descartar seleção
                    </Action>
                  ) : null}
                  {currentPhoto && editing !== "new" && !confirmRemovePhoto ? (
                    <Action
                      type="button"
                      size="sm"
                      variant="danger"
                      icon={Trash2}
                      data-testid="staff-photo-remove"
                      onClick={() => setConfirmRemovePhoto(true)}
                    >
                      Remover foto
                    </Action>
                  ) : null}
                </div>
              </div>
              {photoFile && editing === "new" ? (
                <p data-testid="staff-photo-pending" className="mt-2 text-[13px] text-ink-500">
                  A foto será enviada ao salvar o cadastro. Se a prévia não aparecer (HEIC do iPhone), a conversão
                  acontece no servidor.
                </p>
              ) : null}
              {confirmRemovePhoto ? (
                <div
                  data-testid="staff-photo-remove-confirm"
                  className="mt-3 space-y-2.5 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-3"
                >
                  <p className="text-[13.5px] text-ink-800">
                    Remover a foto deste funcionário? O avatar com as iniciais voltará a ser exibido.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Action
                      type="button"
                      size="sm"
                      variant="danger"
                      data-testid="staff-photo-remove-confirm-yes"
                      disabled={uploading}
                      onClick={() => removePhoto(editing)}
                    >
                      Confirmar remoção
                    </Action>
                    <Action
                      type="button"
                      size="sm"
                      variant="secondary"
                      data-testid="staff-photo-remove-cancel"
                      onClick={() => setConfirmRemovePhoto(false)}
                    >
                      Cancelar
                    </Action>
                  </div>
                </div>
              ) : null}
            </Field>
            <div className="flex flex-wrap gap-2.5 sm:col-span-2">
              <Action type="submit" icon={Save} data-testid="staff-save-button" disabled={saving}>
                {saving ? "Salvando…" : "Salvar"}
              </Action>
              <Action type="button" variant="secondary" data-testid="staff-cancel-button" onClick={() => setEditing(null)}>
                Cancelar
              </Action>
            </div>
          </form>
        </Panel>
      ) : null}

      <SearchField
        testId="staff-search-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Buscar por nome, usuário ou e-mail"
        className="max-w-sm"
      />

      {filtered.length === 0 ? (
        <EmptyState
          testId="staff-empty"
          icon={Users}
          title={staff.length === 0 ? "Nenhum funcionário cadastrado" : "Nenhum funcionário com essa busca"}
          description="Cadastre as contas da equipe que precisam acessar o portal."
          action={
            <Action variant="secondary" size="sm" onClick={startCreate}>
              Novo funcionário
            </Action>
          }
        />
      ) : (
        <DataTable
          testId="staff-table"
          columns={[
            { key: "name", label: "Nome" },
            { key: "username", label: "Usuário" },
            { key: "email", label: "E-mail", className: "hidden md:table-cell" },
            { key: "type", label: "Tipo" },
            { key: "ward", label: "Ala" },
            { key: "status", label: "Status" },
            { key: "actions", label: "Ações" },
          ]}
        >
          {filtered.map((p) => (
            <Row key={p.id} testId={`staff-row-${p.username}`}>
              <Cell>
                <span className="flex items-center gap-3">
                  <Avatar
                    name={p.name}
                    photoUrl={p.photo_thumb_url}
                    size={36}
                    testId={`staff-avatar-${p.username}`}
                  />
                  <span>
                    <span className="block font-semibold text-ink-800">{p.name}</span>
                    <span className="mt-0.5 block text-[13px] text-ink-500">{ROLE_LABELS[p.role] || p.role}</span>
                  </span>
                </span>
              </Cell>
              <Cell className="font-mono text-[13.5px]">{p.username}</Cell>
              <Cell className="hidden md:table-cell">{p.email || "—"}</Cell>
              <Cell>{STAFF_TYPE_LABELS[p.staff_type] || "—"}</Cell>
              <Cell data-testid={`staff-ward-${p.username}`}>{p.ward || "—"}</Cell>
              <Cell>
                <StatusBadge tone={p.status === "active" ? "active" : "neutral"} testId={`staff-status-${p.username}`}>
                  {p.status === "active" ? "Ativo" : "Inativo"}
                </StatusBadge>
              </Cell>
              <Cell>
                <div className="flex flex-wrap gap-2">
                  <Action size="sm" variant="secondary" data-testid={`staff-edit-${p.username}`} onClick={() => startEdit(p)}>
                    Editar
                  </Action>
                  {p.id !== user?.id ? (
                    <Action
                      size="sm"
                      variant={p.status === "active" ? "danger" : "secondary"}
                      icon={Power}
                      data-testid={`staff-toggle-${p.username}`}
                      onClick={() => toggleStatus(p)}
                    >
                      {p.status === "active" ? "Desativar" : "Ativar"}
                    </Action>
                  ) : null}
                </div>
              </Cell>
            </Row>
          ))}
        </DataTable>
      )}
    </div>
  );
}
